<template>
  <div class="row">
    <PublicHeader :title="nomeDaSecretaria">
      <div class="col-xs-12 col-sm-6">
        <p>
          A execução do Plano de Governo é uma tarefa complexa, dividida entre as
          diferentes Secretarias Municipais. Cada secretaria é responsável por
          colocar em prática os compromissos relacionados à sua área de atuação,
          como Saúde, Educação, Segurança, entre outras.
        </p>
        <p>
          Nesta página, você pode visualizar de forma focada todos os compromissos
          e metas que estão sob a responsabilidade direta desta secretaria,
          facilitando o acompanhamento e a fiscalização das ações setoriais.
        </p>
      </div>
    </PublicHeader>

    <div class="container">
      <Breadcrumb :links="[
        { label: 'Plano de Governo', link: '/planodegoverno' },
        { label: secretariaId, }
      ]" />

      <div class="row">
        <div class="col-xs-12">
          <h2>Resumo</h2>
        </div>

        <div class="col-xs-12 col-sm-6">
          <p class="text-lg">
            Para o do período de
            <select
              name="plano-governo-secretaria"
              id="plano-governo-secretaria"
              v-model="anoPlano"
              @change="fetchData"
            >
              <option value="2025-2028">2025-2028</option>
              <option value="2021-2024">2021-2024</option>
            </select>, a 
            <ValorDestaque :value="nomeDaSecretaria" /> 
            é responsável por um total de
            <ValorDestaque :value="compromissos.length" :isLoading="isLoading" /> compromissos.
          </p>
          <p v-if="!isLoading && statusTexto" class="text-lg">
            {{ statusTexto }}
          </p>
        </div>

        <div class="col-xs-12 col-sm-6">
          <PieChart 
            :chartData="chartData"
            :gradiente="false"
            :css-classes="'chartWrapper'"
            :options="chartOptions"
          />
        </div>
      </div>

      <div class="row" style="margin-top: 40px;">
        <div class="col-xs-12">
          <h2>Compromissos</h2>
        </div>

        <div class="col-xs-12" style="margin-bottom: 20px;">
          <div class="filtro-wrapper">
            <input 
              type="text"
              v-model="termoPesquisa"
              class="form-control"
              placeholder="Pesquisar por descritivo ou status..."
              @keyup.enter="pesquisar" 
            />
            <button class="btn btn-primary" @click="pesquisar">Pesquisar</button>
          </div>
        </div>

        <div class="col-xs-12">
          <div class="table-wrapper">
            <data-table-public
              ref="compromissosTable"
              :has-auth="false"
              title="Lista de Compromissos"
              api-url="planodegoverno/compromissos"
              :fields="model.columns"
              :append-params="filtrosDataTable"
              :show-empty="true"
            >
              <template slot="columncustom" slot-scope="props">
                <span :class="['status-tag', getStatusClass(props.rowData.status)]">{{ props.rowData.status }}</span>
              </template>

              <template slot="rowrender" slot-scope="props">
                <button
                  v-if="props.rowData.acoes && props.rowData.acoes.length > 0"
                  class="btn btn-primary btn-sm"
                  @click="abrirSidePanel(props.rowData.acoes)"
                >
                  Ver Ações
                </button>
                <span v-else></span>
              </template>
            </data-table-public>
          </div>
        </div>
      </div>
    </div>

    <div :class="['side-panel-overlay', { 'is-open': isSidePanelOpen }]" @click="fecharSidePanel">
      <div class="side-panel-content" @click.stop>
        <button class="side-panel-close" @click="fecharSidePanel">&times;</button>
        <h3>Ações do compromisso</h3>

        <div class="side-panel-body">
          <div v-if="sidePanelChartData" class="modal-chart-container">
            <PieChart 
              :chartData="sidePanelChartData"
              :options="sideChartOptions"
              :height="150"
            />
          </div>

          <div class="filtro-wrapper filtro-modal">
            <input 
              type="text"
              v-model="termoPesquisaAcoes"
              class="form-control"
              placeholder="Pesquisar por descritivo ou status..."
            />
          </div>
    
          <div v-if="acoesFiltradas && acoesFiltradas.length > 0" class="acoes-list">
            <div v-for="acao in acoesFiltradas" :key="acao.id" class="acao-item">
              <div class="acao-info">
                <p class="acao-descricao">{{ acao.descritivo }}</p>
                <p class="acao-unidade"><strong>Unidade:</strong> {{ acao.nome_unidade }}</p>
              </div>
              <div class="acao-status">
                <span :class="['status-tag', getStatusClass(acao.status)]">{{ acao.status }}</span>
              </div>
            </div>
          </div>

          <div v-else>
            <p>Nenhuma ação encontrada.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import DataTablePublic from '@/components/common/DataTablePublic';
import { PublicHeader, Breadcrumb, ValorDestaque, PieChart } from '../../../components';
import colors from '@/utils/colors'

export default {
  name: 'PorSecretaria',
  components: {
    DataTablePublic,
    PublicHeader,
    Breadcrumb,
    ValorDestaque,
    PieChart
  },
  data() {
    return {
      anoPlano: '2025-2028',
      termoPesquisa: '',
      termoPesquisaAcoes: '',

      isSidePanelOpen: false,
      acoesSidePanel: [],
      sidePanelChartData: null,
      
      model: {
        columns: [
          {
            name: 'descritivo',
            title: 'Descritivo',
            dataClass: 'align-middle',
          },
          {
            name: '__slot:column',
            title: 'Status',
            titleClass: 'text-center',
            dataClass: 'text-center align-middle',
          },
          {
            name: '__slot:actions',
            title: 'Ações',
            titleClass: 'text-center',
            dataClass: 'text-center align-middle',
          },
        ]
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom'
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              const currentValue = dataset.data[tooltipItem.index];
              const percentage = ((currentValue / total) * 100).toFixed(1) + '%';
              const label = data.labels[tooltipItem.index] || '';
              return `${label}: ${currentValue} (${percentage})`;
            }
          }
        },
        plugins: {
          datalabels: {
            color: function(context) {
              const backgroundColor = context.dataset.backgroundColor[context.dataIndex];

              if (backgroundColor === '#222222' || backgroundColor === '#AD68A0' || backgroundColor === '#5C8A95') {
                return '#FFFFFF';
              }
            },
            font: {
              weight: 'bold'
            },
          }
        }
      },
      sideChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'right'
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              const currentValue = dataset.data[tooltipItem.index];
              const percentage = ((currentValue / total) * 100).toFixed(1) + '%';
              const label = data.labels[tooltipItem.index] || '';
              return `${label}: ${currentValue} (${percentage})`;
            }
          }
        },
        plugins: {
          datalabels: {
            color: function(context) {
              const backgroundColor = context.dataset.backgroundColor[context.dataIndex];

              if (backgroundColor === '#222222' || backgroundColor === '#AD68A0' || backgroundColor === '#5C8A95') {
                return '#FFFFFF';
              }
            },
            font: {
              weight: 'bold'
            },
          }
        }
      }
    };
  },
  created() {
    this.fetchData();
  },
  watch: {
    anoPlano() {
      this.fetchData();
    },
    secretariaId() {
      this.fetchData();
    }
  },
  methods: {
    ...mapActions('plano_governo', ['loadCompromissosSecretaria']),
    fetchData() {
      if (!this.secretariaId) return;

      this.loadCompromissosSecretaria({
        periodo: this.anoPlano,
        secretariaId: this.secretariaId
      });

      this.$nextTick(() => {
        if (this.$refs.compromissosTable) {
          this.$refs.compromissosTable.refresh();
        }
      });
    },
    pesquisar() {
      this.$refs.compromissosTable.refresh();
    },
    getStatusClass(status) {
        if (!status) return '';

        const statusSemAcentos = status
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')

        const statusNormalized = statusSemAcentos
          .toLowerCase()
          .replace(/\s+/g, '-');

        return `status-${statusNormalized}`;
    },
    abrirSidePanel(acoes) {
      const counts = acoes.reduce((acc, acao) => {
        const status = acao.status || 'Não Informado';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      this.sidePanelChartData = {
        labels: Object.keys(counts),
        datasets: [{
          data: Object.values(counts),
          backgroundColor: colors.coresHexa,
          color: '#FFF'
        }]
      }

      this.acoesSidePanel = acoes;
      this.isSidePanelOpen = true;
    },
    fecharSidePanel() {
      this.isSidePanelOpen = false;

      setTimeout(() => {
        this.acoesSidePanel = [];
        this.sidePanelChartData = null;
        this.termoPesquisaAcoes = '';
      }, 300);
    }
  },
  computed: {
    ...mapState('plano_governo', {
      compromissos: 'compromissosSecretaria',
      isLoading: 'loadingSecretaria'
    }),
    secretariaId() {
      return this.$route.params.secretaria;
    },
    filtrosDataTable() {
      const filtros = {
        unidade_executora: this.secretariaId,
        periodo: this.anoPlano
      };

      if (this.termoPesquisa) {
        filtros.pesquisa = this.termoPesquisa;
      }

      return filtros;
    },
    nomeDaSecretaria() {
      if (this.compromissos && this.compromissos.length > 0) {
        return this.compromissos[0].nome_unidade_executora;
      }
      return this.secretariaId;
    },
    statusCounts() {
      if (!this.compromissos || this.compromissos.length === 0) {
        return {};
      }

      return this.compromissos.reduce((acc, compromisso) => {
        const status = compromisso.status || 'Não Informado';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
    },
    statusTexto() {
      const counts = this.statusCounts;

      if (Object.keys(counts).length === 0) {
        return '';
      }

      const ordemStatus = ['Não Iniciado', 'Em andamento', 'Concluído', 'Cancelado'];
      const frases = [];

      ordemStatus.forEach(status => {
        const count = counts[status] || 0;

        if (count > 0) {
          if (status === 'Não Iniciado') {
            if (count > 1) {
              frases.push(`${count} não foram iniciados`);
            } else {
              frases.push(`${count} não foi iniciado`);
            }
          }

          if (status === 'Em andamento') {
            if (count > 1) {
              frases.push(`${count} estão em andamento`);
            } else {
              frases.push(`${count} está em andamento`);
            }
          }

          if (status === 'Concluído') {
            if (count > 1) {
              frases.push(`${count} foram concluídos`);
            } else {
              frases.push(`${count} foi concluído`);
            }
          }

          if (status === 'Cancelado') {
            if (count > 1) {
              frases.push(`${count} foram cancelados`);
            } else {
              frases.push(`${count} foi cancelado`);
            }
          }
        }
      })

      if (frases.length === 0) {
        return '';
      } else if (frases.length === 1) {
        return `Deste total, ${frases[0]}.`;
      } else if (frases.length === 2) {
        return `Deste total, ${frases[0]} e ${frases[1]}.`;
      } else {
        const last = frases.pop();
        return `Deste total, ${frases.join(', ')}, e ${last}.`;
      }
    },
    chartData() {
      const counts = this.statusCounts;

      if (Object.keys(counts).length === 0) {
        return { labels: [], datasets: [] };
      }

      const labels = Object.keys(counts);
      const data = Object.values(counts);

      return {
        labels: labels,
        datasets: [
          {
            data: data,
            label: 'Status dos Compromissos',
            backgroundColor: colors.coresHexa,
            color: '#FFF'
          }
        ]
      }
    },
    acoesFiltradas() {
      if (!this.acoesSidePanel || this.acoesSidePanel.length === 0) {
        return [];
      }

      if (!this.termoPesquisaAcoes) {
        return this.acoesSidePanel;
      }

      const termo = this.termoPesquisaAcoes.toLowerCase();

      return this.acoesSidePanel.filter(acao => {
        const descritivo = acao.descritivo ? acao.descritivo.toLowerCase() : '';
        const status = acao.status ? acao.status.toLowerCase() : '';
        const unidae = acao.unidae ? acao.unidae.toLowerCase() : '';
        const nomeUnidade = acao.nome_unidade ? acao.nome_unidade.toLowerCase() : '';

        return descritivo.includes(termo) || status.includes(termo) || unidae.includes(termo) || nomeUnidade.includes(termo);
      });
    }
  }
};
</script>

<style scoped>
#plano-governo-secretaria {
  background-color: transparent;
  border: 1px solid #468b35;
  border-radius: 5px;
  padding: 5px 10px;
  margin: 0 5px;
  font-size: 1em;
}
#plano-governo-secretaria:hover {
  border: 1px solid #a1be4d;
  cursor: pointer;
} 
option {
  color: #000;
}

.chartWrapper {
  max-height: 350px;
}

.table-wrapper {
  overflow-x: auto;
}

.filtro-wrapper {
  display: flex;
  gap: 10px;
}
.filtro-modal {
  margin-bottom: 20px;
}

.acao-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 15px 0;
  border-bottom: 1px solid #e9ecef;
}
.acao-item:last-child {
  border-bottom: none;
}
.acao-info {
  flex-grow: 1;
  margin-right: 20px;
}
.acao-descricao {
  margin: 0 0 10px 0;
  font-size: 1.05em;
  color: #333;
}
.acao-detalhes span {
  font-size: 0.9em;
  color: #555;
  margin-right: 20px;
}
.acao-unidade {
  font-size: 0.9em;
  color: #555;
  font-style: italic;
}
.acao-unidade strong {
  font-style: normal;
}
.acao-status {
  flex-shrink: 0;
}

.status-tag {
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 0.85em;
  font-weight: bold;
  color: #fff;
  text-transform: capitalize;
  white-space: nowrap;
  display: inline-block
}

.status-nao-iniciado {
  background-color: #6c757d;
}

.status-em-andamento {
  background-color: #007bff;
}

.status-concluida, .status-concluido {
  background-color: #28a745;
}

.status-cancelada, .status-cancelado, 
.status-rejeitada {
  background-color: #dc3545;
}

.side-panel-overlay {
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 2000;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.4s ease-in-out, visibility 0.4s;
}

.side-panel-overlay.is-open {
  visibility: visible;
  opacity: 1;
}

.side-panel-content {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 500px;
  max-width: 90%;
  background-color: #f8f9fa;
  box-shadow: -2px 0 15px rgba(0,0,0,0.2);
  padding: 20px;
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.4s ease;
}

.side-panel-overlay.is-open .side-panel-content {
  transform: translateX(0);
}

.side-panel-close {
    position: absolute;
    top: 10px;
    right: 15px;
    border: none;
    background: transparent;
    font-size: 2.5em;
    font-weight: 300;
    color: #888;
    cursor: pointer;
    line-height: 1;
}
.side-panel-close:hover {
  color: #333;
}

.side-panel-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
  padding-right: 30px;
}

.side-panel-body {
  flex-grow: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.modal-chart-container {
  width: 100%;
  margin-bottom: 20px;
}

.acoes-list {
  flex-grow: 1;
}
</style>


// WEBPACK FOOTER //
// src/views/public/plano_governo/PorSecretaria.vue